import React, { useEffect, useState } from 'react';
import TextInputWithLabel from "../../components/TextInputWithLabel";
import { postToHub } from "../../actions/networkActions";
import PinInput from "../../components/passengerInformation/PinInput";
import { isPinValid, isValidEmail } from "../../utils/miscUtils";
import FormLockBase from "../../components/passengerInformation/FormLockBase";
import usePreCruisePassenger from '../../hooks/usePreCruisePassenger';
import { Trans, useLingui } from '@lingui/react/macro';
import { navigate } from '../../utils/navigationUtils';

function PassengerLockByEmailForm(props) {
    const [lock, setLock] = useState(null);
    const [pin, setPin] = useState(null);
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [submitInProgress, setSubmitInProgress] = useState(false);
    
    const {t} = useLingui();
    
    const {passengerId, type} = props.params;
    const passenger = usePreCruisePassenger(passengerId);
    const isMedicalMobilityPage = type === "medicalAndMobility";
    const successUrl = t({id: "/precruise/passengers"}) + `${isMedicalMobilityPage ? "/medicalAndMobility" : ""}`;

    useEffect(() => {
        resetForm();
    }, [lock]);

    const resetForm = (email="") => {
        setEmail(email);
        setPin(null);
        setError("");
    };

    const handleSubmitEmail = async () => {
        if (!isValidEmail(email)) {
            setError(t({id: "please_enter_a_valid_email_address"}));
            return;
        }

        if (submitInProgress) return;

        setSubmitInProgress(true);
        await postToHub('precruise/passenger/otp', {
            passengerId,
            emailId: email,
        }, successCallback, errorCallback);
        setSubmitInProgress(false);

        function successCallback(){
            // OTP sent Successfully.. so collect pin
            setPin(new Array(6).fill(""));
        }

        function errorCallback(){
            // Failed to send OTP
            setError(t({id: "there_has_been_error_sending_code_try_again"}));
        }
    };

    const handleSubmitOTP = async () => {
        if (!isPinValid(pin)) {
            setError(t({id: "invalid_code"}));
            return;
        }

        if (submitInProgress) return;

        setSubmitInProgress(true);
        const response = await postToHub(
            {
                path: 'precruise/passenger/otp/validate',
                logoutOn403: false,
            },
            {
                passengerId,
                otp: pin.join(""),
            }
        );
        setSubmitInProgress(false);

        if (response==="") {
            navigate(successUrl);
        } else {
            setError(t({id: "invalid_code"}));
        }
    };

    const handleoptOut = async () => {
        if (submitInProgress) return;

        setSubmitInProgress(true);
        const response = await postToHub('precruise/passenger/otp/optOut', {passengerId});
        setSubmitInProgress(false);

        if (response==="") {
            navigate(successUrl);
        } else {
            setError(t({id: "there_has_been_error_try_again"}));
        }
    }

    const handleSubmit = () => {
        setError(""); // Clear Errors on submit
        if (lock) {
            pin ? handleSubmitOTP() : handleSubmitEmail();
        } else if(passenger.secureReset) {
            // If user in secureReset flow and opt out from lock.. 
            handleoptOut();
        }else{
            navigate(successUrl);
        }
    };

    const isFormValid = lock !== null && (lock ? (pin ? isPinValid(pin) : isValidEmail(email)) : true);

    return (
        <div className="container mx-auto">
            <FormLockBase lock={lock} setLock={setLock} isMedicalMobilityPage={isMedicalMobilityPage}/>

            {lock && (
                <div className="mt-8">
                    {pin ? (
                        <>
                            <div className="text-lg text-grey-darker mb-2">
                                <Trans id="enter_otp_below">Please enter the One Time Password below.</Trans>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 items-start">
                                <p className="mb-0">
                                    <Trans id="otp_sent_to_email">OTP was sent to email: <strong>{email}</strong></Trans>
                                </p>
                                <button className="text-primary underline text-sm" onClick={()=>resetForm(email)}>
                                    <Trans id="incorrect_email">Incorrect Email?</Trans>
                                </button>
                            </div>
                            <PinInput
                                autoFocus
                                pin={pin}
                                setPin={setPin}
                                submit={handleSubmit}
                                idPrefix="pin"
                            />
                        </>
                    ) : (
                        <>
                            <div className="text-lg text-grey-darker mb-2">
                                <Trans id="please_provide_email">Please provide your Email Address.</Trans>
                            </div>
                            <p><Trans id="we_will_send_otp">We will send a One Time Password (OTP) to your email address for verification.</Trans></p>
                            <TextInputWithLabel
                                type="email"
                                value={email}
                                _onChange={setEmail}
                                placeholder={t({id: "enter_email"})}
                            />
                        </>
                    )}
                </div>
            )}

            {error && <div className="mt-4 text-red">{error}</div>}

            <div className="mt-6 mb-8 xl:mb-12">
                <button
                    className={`w-mobile action-button ${isFormValid && !submitInProgress ? "action-button-enabled" : "action-button-disabled"}`}
                    disabled={!isFormValid}
                    onClick={handleSubmit}
                >
                    {lock ? (pin ? t({id: "verify_and_back_to_dashboard"}) : t({id: "send_otp"})) : t({id: "submit_and_back_to_dashboard"})}
                </button>
            </div>
        </div>
    );
}

export default PassengerLockByEmailForm;